import { parseDate, type CalendarDate } from '@internationalized/date';
import clsx from 'clsx';
import { Calendar, CalendarRange, ChevronDown } from 'lucide-react';
import { HSDatepicker } from 'preline';
import {
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from '../buttons';

type CalendarDateLike = {
  toString: () => string;
};

type DateRangeValue = {
  start?: CalendarDate;
  end?: CalendarDate;
} | null;

type ComparisonSidebarMode = 'previous' | 'custom';

type DatePickerProps = {
  value?: DateRangeValue;
  defaultValue?: DateRangeValue;
  onChange?: (value: DateRangeValue) => void;
  minValue?: CalendarDateLike | null;
  maxValue?: CalendarDateLike | null;
  isDisabled?: boolean;
  className?: string;
  comparisonSidebarMode?: ComparisonSidebarMode;
  onPreviousPeriodClick?: () => void;
  getFooterMessage?: (value: DateRangeValue) => string | null;
};

type PresetId =
  | 'latest-7-days'
  | 'latest-14-days'
  | 'latest-30-days'
  | 'this-month'
  | 'last-month';

type PresetOption = {
  id: PresetId;
  label: string;
  value: DateRangeValue;
};

type PrelineDatepickerState = {
  selectedDates?: unknown;
  context?: {
    selectedDates?: unknown;
  };
};

type PrelineDatepickerClickEvent = {
  target: EventTarget | null;
};

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const isIsoDateString = (value: string | undefined | null): value is string =>
  Boolean(value && ISO_DATE_PATTERN.test(value));

const getIsoFromCalendarDate = (value?: CalendarDateLike | null) => {
  const iso = value?.toString?.();
  return isIsoDateString(iso) ? iso : null;
};

const toUtcDate = (iso: string) => new Date(`${iso}T00:00:00.000Z`);

const toIsoDate = (value: Date) => value.toISOString().slice(0, 10);

const addUtcDays = (value: Date, days: number) => {
  const next = new Date(value);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
};

const startOfUtcMonth = (value: Date) =>
  new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), 1));

const endOfUtcMonth = (value: Date) =>
  new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth() + 1, 0));

const buildRangeValue = (
  startIso?: string | null,
  endIso?: string | null
): DateRangeValue => {
  if (!isIsoDateString(startIso)) return null;

  return {
    start: parseDate(startIso),
    end: isIsoDateString(endIso) ? parseDate(endIso) : undefined,
  };
};

const normalizeRangeValue = (
  value: DateRangeValue,
  minIso: string | null,
  maxIso: string | null,
  fillMissingEnd = true
): DateRangeValue => {
  const startIso = getIsoFromCalendarDate(value?.start);
  const endIso = getIsoFromCalendarDate(value?.end);

  if (!startIso) return null;

  const clampIso = (iso: string) => {
    if (minIso && iso < minIso) return minIso;
    if (maxIso && iso > maxIso) return maxIso;
    return iso;
  };

  const boundedStart = clampIso(startIso);
  const boundedEnd = isIsoDateString(endIso)
    ? clampIso(endIso)
    : fillMissingEnd
      ? boundedStart
      : undefined;

  if (!boundedEnd) {
    return buildRangeValue(boundedStart);
  }

  if (boundedStart <= boundedEnd) {
    return buildRangeValue(boundedStart, boundedEnd);
  }

  return buildRangeValue(boundedEnd, boundedStart);
};

const getRangeKey = (value: DateRangeValue) => {
  const startIso = getIsoFromCalendarDate(value?.start);
  const endIso = getIsoFromCalendarDate(value?.end);
  return `${startIso ?? ''}:${endIso ?? ''}`;
};

const formatDisplayDate = (value?: CalendarDateLike | null): string => {
  const iso = getIsoFromCalendarDate(value);
  if (!iso) return '-- --- ----';

  const date = toUtcDate(iso);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const getPresetOptions = (
  minIso: string | null,
  maxIso: string | null
): PresetOption[] => {
  if (!maxIso) return [];

  const maxDate = toUtcDate(maxIso);

  const clamp = (startIso: string, endIso: string) =>
    normalizeRangeValue(buildRangeValue(startIso, endIso), minIso, maxIso);

  return [
    {
      id: 'latest-7-days',
      label: 'Latest 7 Days',
      value: clamp(toIsoDate(addUtcDays(maxDate, -6)), maxIso),
    },
    {
      id: 'latest-14-days',
      label: 'Latest 14 Days',
      value: clamp(toIsoDate(addUtcDays(maxDate, -13)), maxIso),
    },
    {
      id: 'latest-30-days',
      label: 'Latest 30 Days',
      value: clamp(toIsoDate(addUtcDays(maxDate, -29)), maxIso),
    },
    {
      id: 'this-month',
      label: 'This Month',
      value: clamp(toIsoDate(startOfUtcMonth(maxDate)), maxIso),
    },
    {
      id: 'last-month',
      label: 'Last Month',
      value: clamp(
        toIsoDate(startOfUtcMonth(addUtcDays(startOfUtcMonth(maxDate), -1))),
        toIsoDate(endOfUtcMonth(addUtcDays(startOfUtcMonth(maxDate), -1)))
      ),
    },
  ];
};

const extractSelectedDates = (input: unknown): string[] => {
  if (!Array.isArray(input)) return [];

  const dates = input.flatMap((entry) => {
    if (typeof entry !== 'string') return [];

    const matches = entry.match(/\d{4}-\d{2}-\d{2}/g);
    return matches ?? [];
  });

  return Array.from(
    new Set(dates.filter((value) => ISO_DATE_PATTERN.test(value)))
  ).slice(0, 2);
};

const getMonthStart = (iso: string) => {
  const date = toUtcDate(iso);
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
};

const clampCalendarView = (
  selectedMonth: number,
  selectedYear: number,
  minIso: string | null,
  maxIso: string | null
): { selectedMonth: number; selectedYear: number } => {
  const selectedMonthStart = new Date(Date.UTC(selectedYear, selectedMonth, 1));
  const minMonthStart = minIso ? getMonthStart(minIso) : null;
  const maxMonthStart = maxIso ? getMonthStart(maxIso) : null;
  const maxLeftMonthStart = maxMonthStart
    ? new Date(
        Date.UTC(
          maxMonthStart.getUTCFullYear(),
          maxMonthStart.getUTCMonth() - 1,
          1
        )
      )
    : null;

  if (minMonthStart && selectedMonthStart < minMonthStart) {
    return {
      selectedMonth: minMonthStart.getUTCMonth(),
      selectedYear: minMonthStart.getUTCFullYear(),
    };
  }

  if (maxLeftMonthStart && selectedMonthStart > maxLeftMonthStart) {
    return {
      selectedMonth: maxLeftMonthStart.getUTCMonth(),
      selectedYear: maxLeftMonthStart.getUTCFullYear(),
    };
  }

  return { selectedMonth, selectedYear };
};

const getInitialTwoMonthView = (
  minIso: string | null,
  maxIso: string | null,
  selectedIso?: string | null
) => {
  const now = new Date();
  const currentMonthStart = new Date(
    Date.UTC(now.getFullYear(), now.getMonth(), 1)
  );
  const minMonthStart = minIso ? getMonthStart(minIso) : null;
  const maxMonthStart = maxIso ? getMonthStart(maxIso) : null;
  const maxLeftMonthStart = maxMonthStart
    ? new Date(
        Date.UTC(
          maxMonthStart.getUTCFullYear(),
          maxMonthStart.getUTCMonth() - 1,
          1
        )
      )
    : null;

  const targetMonthStart = isIsoDateString(selectedIso)
    ? getMonthStart(selectedIso)
    : currentMonthStart;
  let selectedMonthStart = isIsoDateString(selectedIso)
    ? new Date(
        Date.UTC(
          targetMonthStart.getUTCFullYear(),
          targetMonthStart.getUTCMonth() - 1,
          1
        )
      )
    : targetMonthStart;

  if (minMonthStart && selectedMonthStart < minMonthStart) {
    selectedMonthStart = minMonthStart;
  }

  if (maxLeftMonthStart && selectedMonthStart > maxLeftMonthStart) {
    selectedMonthStart = maxLeftMonthStart;
  }

  return {
    selectedMonth: selectedMonthStart.getUTCMonth(),
    selectedYear: selectedMonthStart.getUTCFullYear(),
  };
};

const getCustomSelectStyles = () => ({
  shared: {
    toggleClasses:
      'hs-select-disabled:pointer-events-none hs-select-disabled:opacity-50 relative py-1 px-2 pe-6 flex text-nowrap w-full cursor-pointer bg-transparent text-start text-sm font-medium text-slate-700 focus:outline-hidden before:absolute before:inset-0 before:z-1',
    dropdownVerticalFixedPlacement: 'bottom',
    dropdownScope: 'parent',
    dropdownClasses:
      'mt-2 z-50 w-28 min-w-28 max-h-60 p-1 space-y-0.5 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-slate-100 [&::-webkit-scrollbar-thumb]:bg-slate-300',
    optionClasses:
      'p-2 w-full text-sm text-slate-700 cursor-pointer hover:bg-slate-100 rounded-lg focus:outline-hidden focus:bg-slate-100',
    optionTemplate:
      '<div class="flex justify-between items-center w-full"><span data-title></span></div>',
  },
  months: {
    toggleClasses:
      'hs-select-disabled:pointer-events-none hs-select-disabled:opacity-50 relative py-1 px-2 pe-6 flex text-nowrap w-full cursor-pointer bg-transparent text-start text-sm font-medium text-slate-700 focus:outline-hidden before:absolute before:inset-0 before:z-1',
  },
  years: {
    toggleClasses:
      'hs-select-disabled:pointer-events-none hs-select-disabled:opacity-50 relative py-1 px-2 pe-6 flex text-nowrap w-full cursor-pointer bg-transparent text-start text-sm font-medium text-slate-700 focus:outline-hidden before:absolute before:inset-0 before:z-1',
  },
});

const DatePicker = ({
  value,
  defaultValue = null,
  onChange,
  minValue = null,
  maxValue = null,
  isDisabled = false,
  className,
  comparisonSidebarMode,
  onPreviousPeriodClick,
  getFooterMessage,
}: DatePickerProps) => {
  const isControlled = value !== undefined;
  const minIso = getIsoFromCalendarDate(minValue);
  const maxIso = getIsoFromCalendarDate(maxValue);
  const [internalValue, setInternalValue] = useState<DateRangeValue>(
    normalizeRangeValue(defaultValue, minIso, maxIso)
  );
  const committedValue = useMemo(
    () =>
      normalizeRangeValue(
        isControlled ? (value ?? null) : internalValue,
        minIso,
        maxIso
      ),
    [internalValue, isControlled, maxIso, minIso, value]
  );
  const [draftValue, setDraftValue] = useState<DateRangeValue>(committedValue);
  const [isOpen, setIsOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties | undefined>();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const datepickerRef = useRef<HSDatepicker | null>(null);
  const pendingPresetDatesRef = useRef<string[] | null>(null);
  const calendarObserverRef = useRef<MutationObserver | null>(null);

  const presets = useMemo(
    () => getPresetOptions(minIso, maxIso),
    [maxIso, minIso]
  );
  const showComparisonSidebar = Boolean(comparisonSidebarMode);
  const initialViewTargetIso = getIsoFromCalendarDate(
    committedValue?.end ?? committedValue?.start
  );

  const destroyDatepicker = () => {
    const instance = datepickerRef.current;

    if (!instance) return;

    try {
      instance.destroy();
    } catch {
      // Preline can throw during teardown if its internal collection state is missing.
    } finally {
      datepickerRef.current = null;
    }
  };

  const syncCalendarSelection = (value: DateRangeValue) => {
    const vanillaCalendar = (datepickerRef.current as any)?.vanillaCalendar as
      | {
          set: (
            nextState: { selectedDates: string[] },
            options: {
              dates: boolean;
              month: boolean;
              year: boolean;
              time: boolean;
            }
          ) => void;
        }
      | undefined;

    if (!vanillaCalendar) return;

    const selectedDates = extractSelectedDates([
      getIsoFromCalendarDate(value?.start),
      getIsoFromCalendarDate(value?.end),
    ]);

    vanillaCalendar.set(
      {
        selectedDates,
      },
      {
        dates: true,
        month: true,
        year: true,
        time: false,
      }
    );
  };

  const updateDraftFromSelectedDates = (selectedDatesInput: unknown) => {
    const selectedDates = extractSelectedDates(selectedDatesInput);

    if (selectedDates.length === 0) {
      setDraftValue(null);
      return;
    }

    const [startIso, endIso] = selectedDates;
    setDraftValue(
      normalizeRangeValue(
        buildRangeValue(startIso, endIso),
        minIso,
        maxIso,
        false
      )
    );
  };

  useEffect(() => {
    if (isOpen) return;

    const committedKey = getRangeKey(committedValue);
    const draftKey = getRangeKey(draftValue);

    if (draftKey !== committedKey) {
      setDraftValue(committedValue);
    }
  }, [committedValue, draftValue, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (wrapperRef.current?.contains(event.target as Node)) return;
      setDraftValue(committedValue);
      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setDraftValue(committedValue);
      setIsOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [committedValue, isOpen]);

  useEffect(
    () => () => {
      destroyDatepicker();
    },
    []
  );

  useEffect(() => {
    if (isOpen) return;

    destroyDatepicker();

    if (calendarRef.current) {
      calendarRef.current.innerHTML = '';
    }
  }, [isOpen]);

  const syncDraftFromDatepicker = (state?: PrelineDatepickerState) => {
    updateDraftFromSelectedDates(
      state?.context?.selectedDates ??
        state?.selectedDates ??
        datepickerRef.current?.getCurrentState()?.selectedDates
    );
  };

  const applyCalendarElementOverrides = () => {
    const root = calendarRef.current;
    if (!root) return;

    root
      .querySelectorAll<HTMLElement>('[data-vc-date-disabled]')
      .forEach((cell) => {
        const button = cell.querySelector<HTMLButtonElement>('button');

        cell.style.pointerEvents = 'none';
        cell.style.background = 'transparent';
        cell.style.boxShadow = 'none';
        cell.style.borderColor = 'transparent';
        // cell.style.opacity = '0.35';
        cell.style.color = '#D1D5DB';

        if (!button) return;

        button.style.pointerEvents = 'none';
        button.style.background = 'transparent';
        button.style.boxShadow = 'none';
        button.style.border = '0';
        button.style.outline = '0';
        button.style.color = '#D1D5DB';
        button.style.cursor = 'not-allowed';
      });

    root
      .querySelectorAll<HTMLElement>('[data-vc-date-today]')
      .forEach((cell) => {
        const button = cell.querySelector<HTMLButtonElement>('button');
        const isSelected = cell.hasAttribute('data-vc-date-selected');

        if (isSelected) {
          cell.style.background = 'transparent';
          cell.style.boxShadow = 'none';
          cell.style.color = '#fff';
        } else {
          cell.style.background = 'transparent';
          cell.style.boxShadow = 'none';
          cell.style.color = '#4f46e5';
        }

        if (!button) return;

        button.style.boxShadow = 'none';
        button.style.border = '0';
        button.style.outline = '0';
        if (isSelected) {
          button.style.background = '#155dfc';
          button.style.setProperty('color', '#fff', 'important');
        } else {
          button.style.background = 'transparent';
          button.style.color = '#4f46e5';
        }

        if (isSelected) {
          cell.style.setProperty('color', '#fff', 'important');
        }
      });
  };

  const scheduleCalendarElementOverrides = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(applyCalendarElementOverrides);
      requestAnimationFrame(updateArrowVisibility);
    });
  };

  const startCalendarObserver = () => {
    const root = calendarRef.current;
    if (!root) return;

    calendarObserverRef.current?.disconnect();
    calendarObserverRef.current = new MutationObserver(() => {
      enforceCalendarViewBounds();
      scheduleCalendarElementOverrides();
    });

    calendarObserverRef.current.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
    });
  };

  const enforceCalendarViewBounds = () => {
    const vanillaCalendar = (datepickerRef.current as any)?.vanillaCalendar as
      | {
          context?: {
            selectedMonth?: number;
            selectedYear?: number;
          };
          set: (
            nextState: { selectedMonth: number; selectedYear: number },
            options: {
              dates: boolean;
              month: boolean;
              year: boolean;
              time: boolean;
            }
          ) => void;
        }
      | undefined;

    const selectedMonth = vanillaCalendar?.context?.selectedMonth;
    const selectedYear = vanillaCalendar?.context?.selectedYear;

    if (
      !vanillaCalendar ||
      typeof selectedMonth !== 'number' ||
      typeof selectedYear !== 'number'
    ) {
      return;
    }

    const clamped = clampCalendarView(
      selectedMonth,
      selectedYear,
      minIso,
      maxIso
    );
    if (
      clamped.selectedMonth === selectedMonth &&
      clamped.selectedYear === selectedYear
    ) {
      return;
    }

    vanillaCalendar.set(clamped, {
      dates: false,
      month: true,
      year: true,
      time: false,
    });
  };

  const updateArrowVisibility = () => {
    const root = calendarRef.current;
    if (!root) return;

    const prevArrow = root.querySelector<HTMLElement>('[data-vc-arrow="prev"]');
    const nextArrow = root.querySelector<HTMLElement>('[data-vc-arrow="next"]');
    const selectedMonth = (datepickerRef.current as any)?.vanillaCalendar
      ?.context?.selectedMonth;
    const selectedYear = (datepickerRef.current as any)?.vanillaCalendar
      ?.context?.selectedYear;

    if (typeof selectedMonth !== 'number' || typeof selectedYear !== 'number') {
      return;
    }

    const firstVisibleMonth = new Date(
      Date.UTC(selectedYear, selectedMonth, 1)
    );
    const minMonthStart = minIso ? getMonthStart(minIso) : null;
    const maxMonthStart = maxIso ? getMonthStart(maxIso) : null;
    const maxLeftMonthStart = maxMonthStart
      ? new Date(
          Date.UTC(
            maxMonthStart.getUTCFullYear(),
            maxMonthStart.getUTCMonth() - 1,
            1
          )
        )
      : null;

    if (prevArrow) {
      const hidePrev = minMonthStart
        ? firstVisibleMonth <= minMonthStart
        : false;
      prevArrow.style.visibility = hidePrev ? 'hidden' : 'visible';
      prevArrow.style.pointerEvents = hidePrev ? 'none' : 'auto';
    }

    if (nextArrow) {
      const hideNext = maxLeftMonthStart
        ? firstVisibleMonth >= maxLeftMonthStart
        : false;
      nextArrow.style.visibility = hideNext ? 'hidden' : 'visible';
      nextArrow.style.pointerEvents = hideNext ? 'none' : 'auto';
    }
  };

  const updatePopoverPlacement = () => {
    if (!isOpen) {
      setPopoverStyle(undefined);
      return;
    }

    const triggerRect = wrapperRef.current?.getBoundingClientRect();
    if (!triggerRect) return;

    const popoverWidth = popoverRef.current?.offsetWidth ?? 0;
    const viewportMargin = 20;

    // Clamp horizontally so the popover never runs past the right (or left) edge.
    const preferredLeft = triggerRect.left;
    const maxLeft = window.innerWidth - viewportMargin - popoverWidth;
    const clampedLeft = Math.min(
      Math.max(preferredLeft, viewportMargin),
      Math.max(maxLeft, viewportMargin)
    );

    // Clamp vertically too — flip above the trigger if there isn't room below.
    const popoverHeight = popoverRef.current?.offsetHeight ?? 0;
    const spaceBelow = window.innerHeight - triggerRect.bottom - 12;
    const shouldFlipUp =
      spaceBelow < popoverHeight && triggerRect.top - 12 > popoverHeight;
    const top = shouldFlipUp
      ? Math.round(triggerRect.top - popoverHeight - 12)
      : Math.round(triggerRect.bottom + 12);

    setPopoverStyle({
      position: 'fixed',
      top: `${Math.max(top, viewportMargin)}px`,
      left: `${Math.round(clampedLeft)}px`,
      right: 'auto',
      zIndex: 80,
    });
  };

  const renderDatepicker = (nextValue: DateRangeValue) => {
    const container = calendarRef.current;
    if (!container) return;

    destroyDatepicker();
    container.innerHTML = '';

    const selectedDates = extractSelectedDates([
      getIsoFromCalendarDate(nextValue?.start),
      getIsoFromCalendarDate(nextValue?.end),
    ]);
    const initialView = getInitialTwoMonthView(
      minIso,
      maxIso,
      getIsoFromCalendarDate(nextValue?.end ?? nextValue?.start) ??
        initialViewTargetIso
    );

    const instance = new HSDatepicker(container, {
      inputMode: false,
      type: 'multiple',
      mode: 'custom-select',
      displayMonthsCount: 2,
      selectionDatesMode: 'multiple-ranged',
      displayDatesOutside: false,
      dateMin: minIso ?? undefined,
      dateMax: maxIso ?? undefined,
      selectedTheme: 'light',
      applyUtilityClasses: true,
      selectedMonth: initialView.selectedMonth,
      selectedYear: initialView.selectedYear,
      styles: {
        customSelect: getCustomSelectStyles(),
      },
      templates: {
        arrowPrev:
          '<button data-vc-arrow="prev"><svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"></path></svg></button>',
        arrowNext:
          '<button data-vc-arrow="next"><svg class="shrink-0 size-4" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"></path></svg></button>',
      },
      onInit: (calendar: any) => {
        calendar.set(
          {
            selectedMonth: initialView.selectedMonth,
            selectedYear: initialView.selectedYear,
          },
          {
            dates: false,
            month: true,
            year: true,
            time: false,
          }
        );
        if (selectedDates.length) {
          calendar.set(
            {
              selectedDates,
            },
            {
              dates: true,
              month: true,
              year: true,
              time: false,
            }
          );
        }
        enforceCalendarViewBounds();
        scheduleCalendarElementOverrides();
        updatePopoverPlacement();
      },
      onClickDate: (
        calendar: PrelineDatepickerState,
        event?: PrelineDatepickerClickEvent
      ) => {
        const target = event?.target;
        const clickedElement = target instanceof HTMLElement ? target : null;
        const clickedDateCell = clickedElement?.closest('[data-vc-date]');
        const clickedIso = clickedDateCell?.getAttribute('data-vc-date');
        const isDisabledDate = Boolean(
          clickedDateCell?.getAttribute('data-vc-date-disabled')
        );
        const isOutOfAllowedRange = Boolean(
          clickedIso &&
            ((minIso && clickedIso < minIso) || (maxIso && clickedIso > maxIso))
        );

        if (isDisabledDate || isOutOfAllowedRange) {
          requestAnimationFrame(() => {
            syncCalendarSelection(draftValue);
            scheduleCalendarElementOverrides();
            updatePopoverPlacement();
          });
          return;
        }

        syncDraftFromDatepicker(calendar);
        scheduleCalendarElementOverrides();
        updatePopoverPlacement();
      },
      onChangeToInput: (calendar: PrelineDatepickerState) => {
        syncDraftFromDatepicker(calendar);
        enforceCalendarViewBounds();
        scheduleCalendarElementOverrides();
        updatePopoverPlacement();
      },
    });

    datepickerRef.current = instance;
  };

  useEffect(() => {
    if (!isOpen) return;
    renderDatepicker(draftValue);
    startCalendarObserver();
    requestAnimationFrame(updatePopoverPlacement);
    scheduleCalendarElementOverrides();
    return () => {
      calendarObserverRef.current?.disconnect();
      calendarObserverRef.current = null;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !datepickerRef.current || !pendingPresetDatesRef.current) {
      return;
    }

    const selectedDates = pendingPresetDatesRef.current;
    pendingPresetDatesRef.current = null;

    const vanillaCalendar = (datepickerRef.current as any)?.vanillaCalendar as
      | {
          set: Parameters<typeof syncCalendarSelection>[0] extends never
            ? never
            : any;
        }
      | undefined;

    if (!vanillaCalendar) return;

    requestAnimationFrame(() => {
      syncCalendarSelection(
        buildRangeValue(selectedDates[0], selectedDates[1]) ?? draftValue
      );
      updatePopoverPlacement();
      scheduleCalendarElementOverrides();
    });
  }, [draftValue, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleViewportChange = () => {
      updatePopoverPlacement();
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isOpen]);

  const applyDraft = () => {
    const normalized = normalizeRangeValue(draftValue, minIso, maxIso);
    if (!normalized?.start) return;

    if (!isControlled) {
      setInternalValue(normalized);
    }

    onChange?.(normalized);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setDraftValue(committedValue);
    setIsOpen(false);
  };

  const handlePresetClick = (preset: PresetOption) => {
    pendingPresetDatesRef.current = extractSelectedDates([
      getIsoFromCalendarDate(preset.value?.start),
      getIsoFromCalendarDate(preset.value?.end),
    ]);
    setDraftValue(preset.value);
  };

  const handlePreviousPeriodClick = () => {
    onPreviousPeriodClick?.();
    setIsOpen(false);
  };

  const handleTriggerMouseDown = (
    event: ReactMouseEvent<HTMLButtonElement>
  ) => {
    if (event.button !== 0 || isDisabled) return;
    event.preventDefault();
    setDraftValue(committedValue);
    setIsOpen((current) => !current);
  };

  const canApply = Boolean(draftValue?.start);
  const footerMessage = getFooterMessage?.(draftValue);

  return (
    <div ref={wrapperRef} className={clsx('relative', className)}>
      <button
        type="button"
        disabled={isDisabled}
        onMouseDown={handleTriggerMouseDown}
        className={clsx(
          'group flex min-h-[46px] items-center gap-3 rounded-lg border border-transparent bg-indigo-100 px-4 py-3 text-left shadow-xs transition-colors',
          isDisabled
            ? 'cursor-not-allowed opacity-60'
            : 'cursor-pointer hover:bg-indigo-500',
          isOpen ? 'bg-indigo-100' : ''
        )}
      >
        {comparisonSidebarMode ? (
          <div className="flex items-center text-indigo-800 transition-colors group-hover:text-white text-sm ">
            <Calendar className="size-8 shrink-0 mr-2.5" />
            <div>PP:</div>
          </div>
        ) : (
          <CalendarRange className="size-8 shrink-0 text-indigo-800 transition-colors group-hover:text-white" />
        )}
        <span className="min-w-0 flex-1 text-sm font-medium text-indigo-800 transition-colors group-hover:text-white">
          {formatDisplayDate(committedValue?.start)} -{' '}
          {formatDisplayDate(committedValue?.end)}
        </span>
        <ChevronDown className="size-4 shrink-0 text-indigo-800 transition-colors group-hover:text-white" />
      </button>

      {isOpen ? (
        <div
          ref={popoverRef}
          className="date-picker-popover absolute top-[calc(100%+12px)] z-80 overflow-hidden rounded-[20px] border border-slate-200 bg-white shadow-[0_16px_38px_rgba(15,23,42,0.12)]"
          style={popoverStyle}
        >
          <div className="flex flex-col md:flex-row">
            <aside className="hidden border-b border-slate-200 bg-slate-50/80 p-4 md:border-b-0 md:border-r lg:block w-47.5">
              {showComparisonSidebar ? (
                <div className="flex flex-col gap-1.5">
                  <button
                    type="button"
                    onClick={handlePreviousPeriodClick}
                    className={clsx(
                      'w-full whitespace-nowrap rounded-[10px] px-4 py-2.5 text-left text-[14px] leading-6 font-medium text-slate-700 transition-colors hover:bg-white hover:text-slate-950',
                      comparisonSidebarMode === 'previous' &&
                        'bg-white text-slate-950 shadow-xs'
                    )}
                  >
                    Previous Period
                  </button>
                  <button
                    type="button"
                    className={clsx(
                      'w-full whitespace-nowrap rounded-[10px] px-4 py-2.5 text-left text-[14px] leading-6 font-medium text-slate-700 transition-colors hover:bg-white hover:text-slate-950',
                      comparisonSidebarMode === 'custom' &&
                        'bg-white text-slate-950 shadow-xs'
                    )}
                  >
                    Custom Range
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  {presets.map((preset) => {
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => handlePresetClick(preset)}
                        className="w-full whitespace-nowrap rounded-[10px] px-4 py-2.5 text-left text-[14px] leading-6 font-medium text-slate-700 transition-colors hover:bg-white hover:text-slate-950"
                      >
                        {preset.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </aside>

            <div className="min-w-0 flex-1 bg-white">
              <div className="relative flex justify-center overflow-x-auto p-5">
                <div
                  ref={calendarRef}
                  className={clsx('date-picker-calendar-shell min-w-[600px]')}
                />
              </div>

              <div className="flex gap-3 border-t border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-end sm:gap-5 md:px-5">
                {footerMessage ? (
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                    {footerMessage}
                  </span>
                ) : null}
                <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm font-medium text-slate-500">
                  <span>
                    {formatDisplayDate(draftValue?.start)} -{' '}
                    {formatDisplayDate(draftValue?.end)}
                  </span>
                </div>

                <div className="flex items-center justify-end gap-2">
                  <Button
                    type="button"
                    onClick={handleCancel}
                    variant="white"
                    className="py-2.5! px-3!"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="solid"
                    className="py-2.5! px-3!"
                    onClick={applyDraft}
                    disabled={!canApply}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DatePicker;
